import { useDraftGuard } from '@/lib/use-draft-guard';
import { useConfirm } from '@/components/ui/confirm';
import { AI_CREDITS } from '@shared/pricing';
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, Copy, RotateCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkspace } from '@/app/workspace';
import { qk, queryClient, invalidateCallData } from '@/app/query';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/utils';
import type { Call } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Notice, Tag } from '@/components/ui/system';
export function CallAi({ call }: { call: Call }) {
  const { org, orgId, refreshOrg } = useWorkspace();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [applied, setApplied] = useState<string[]>([]);
  const record = useQuery({
    queryKey: qk.aiCall(call.id),
    queryFn: () => api.ai.getAnalysisForCall(call.id),
    refetchInterval: (q) => (q.state.data && !api.ai.isAiSettled(q.state.data) ? 3000 : false),
  });
  const queue = useMutation({
    mutationFn: () => api.ai.queueAnalysis(orgId, call.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.aiCall(call.id) });
      setOpen(true);
      setApplied([]);
      await refreshOrg();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const apply = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string | string[] }) => {
      if (key === 'summary') await api.calls.updateCall(orgId, call.id, { note: String(value) });
      else {
        const c = await api.contacts.getContact(orgId, call.phone);
        if (key === 'name') await api.contacts.saveIdentity(orgId, call.phone, { name: String(value) });
        if (key === 'person')
          await api.contacts.saveIdentity(orgId, call.phone, {
            note: [c?.note, c?.note?.includes(String(value)) ? '' : String(value)].filter(Boolean).join('\n'),
          });
        if (key === 'labels')
          await api.contacts.saveIdentity(orgId, call.phone, {
            labels: [...new Set([...(c?.labels || []), ...(value as string[])])],
          });
      }
    },
    onSuccess: async (_, v) => {
      setApplied((a) => [...a, v.key]);
      await invalidateCallData(orgId);
      toast.success('Suggestion saved');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  useDraftGuard(false, apply.isPending || queue.isPending);
  const saveSuggestion = (key: string, value: string) => {
    const current = key === 'summary' ? call.note : key === 'name' ? call.name : '';
    if (current && current !== value)
      confirm({
        title: key === 'summary' ? 'Replace this call note?' : 'Replace this contact name?',
        description: (
          <div className="form-stack">
            <p>
              <strong>Current:</strong> {current}
            </p>
            <p>
              <strong>AI suggestion:</strong> {value}
            </p>
            <p>Review the suggestion before replacing your saved text.</p>
          </div>
        ),
        tone: 'primary',
        confirmLabel: 'Replace saved text',
        action: () => apply.mutateAsync({ key, value }),
      });
    else apply.mutate({ key, value });
  };
  const needed = Math.max(1, Math.ceil(call.duration / AI_CREDITS.secondsPerCredit));
  if (record.isPending) return <p className="call-ai-status">Loading AI analysis…</p>;
  if (record.error)
    return (
      <Notice tone="warning">
        Couldn’t load analysis.{' '}
        <Button size="xs" variant="ghost" onClick={() => record.refetch()}>
          Retry
        </Button>
      </Notice>
    );
  const r = record.data;
  if (!r)
    return org.transcriptionEnabled ? (
      <div className="call-ai-status">
        <span>
          <Sparkles size={13} />
          AI analysis · Summary &amp; transcript ·{needed} {needed === 1 ? 'credit' : 'credits'}
        </span>
        <Button
          size="xs"
          variant="secondary"
          loading={queue.isPending}
          disabled={org.transcriptionCredits < needed}
          onClick={() => queue.mutate()}
        >
          Analyze
        </Button>
        {org.transcriptionCredits < needed && <Link to="/ai">Top up credits</Link>}
      </div>
    ) : (
      <p className="call-ai-status">
        <Link to="/ai">Turn on AI analysis</Link>
      </p>
    );
  if (!api.ai.isAiSettled(r))
    return (
      <p className="call-ai-status">
        <Sparkles size={13} />
        AI analysis · Analyzing recording…
      </p>
    );
  const a = r.status === 'completed' ? r.analysis : null;
  if (!a)
    return (
      <div className="call-ai-status">
        <span>{api.ai.aiErrorText(r) || 'Analysis unavailable'}</span>
        {org.transcriptionEnabled && r.status !== 'skipped' && (
          <Button
            size="xs"
            variant="secondary"
            loading={queue.isPending}
            disabled={org.transcriptionCredits < needed}
            onClick={() => queue.mutate()}
          >
            Try again
          </Button>
        )}
      </div>
    );
  const labels = api.ai.suggestedLabels(a);
  const tasks = api.ai.suggestedTasks(a);
  const suggestion = (key: string, title: string, value: string) => (
    <div className="call-ai-suggestion">
      <div>
        <strong>{title}</strong>
        <p>{value}</p>
      </div>
      <Button
        size="xs"
        variant="secondary"
        disabled={apply.isPending || applied.includes(key) || (key === 'summary' && call.note === value)}
        loading={apply.isPending && apply.variables?.key === key}
        onClick={() => saveSuggestion(key, value)}
      >
        {applied.includes(key) || (key === 'summary' && call.note === value) ? 'Saved' : 'Apply'}
      </Button>
    </div>
  );
  return (
    <div className="call-ai-panel">
      <div className="call-ai-heading">
        <span>
          <Sparkles size={14} />
          AI analysis
          <small>Ready</small>
        </span>
        <Button
          size="xs"
          variant="ghost"
          loading={queue.isPending}
          disabled={!org.transcriptionEnabled || org.transcriptionCredits < needed}
          title={`Analyze again · ${needed} credits`}
          onClick={() => queue.mutate()}
        >
          <RotateCw size={12} />
          Analyze again
        </Button>
      </div>
      <button
        className="call-ai-toggle"
        aria-expanded={open}
        onClick={() => {
          setOpen(!open);
          void refreshOrg();
        }}
      >
        <Sparkles size={14} />
        <span>{a.call_note || 'Transcript ready'}</span>
        <ChevronDown size={14} className={open ? 'rotate-180' : ''} />
      </button>
      {open && (
        <div className="call-ai-body">
          {a.transcription && (
            <section>
              <div className="flex justify-between items-center">
                <strong>Transcript</strong>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(String(a.transcription));
                      toast.success('Transcript copied');
                    } catch {
                      toast.error('Could not copy transcript');
                    }
                  }}
                >
                  <Copy size={12} />
                  Copy
                </Button>
              </div>
              <p className="call-ai-transcript">{a.transcription}</p>
            </section>
          )}
          {a.caller_name && suggestion('name', 'Contact name', String(a.caller_name))}
          {a.call_note && suggestion('summary', 'Save summary as call note', String(a.call_note))}
          {a.person_note && suggestion('person', 'Add to contact note', String(a.person_note))}
          {labels.length > 0 && (
            <div className="call-ai-suggestion">
              <div className="flex flex-wrap gap-1">
                {labels.map((l) => (
                  <Tag key={l}>{l}</Tag>
                ))}
              </div>
              <Button
                size="xs"
                variant="secondary"
                disabled={apply.isPending || applied.includes('labels')}
                loading={apply.isPending && apply.variables?.key === 'labels'}
                onClick={() => apply.mutate({ key: 'labels', value: labels })}
              >
                {applied.includes('labels') ? 'Added' : 'Add labels'}
              </Button>
            </div>
          )}
          {tasks.length > 0 && (
            <section>
              <strong>Follow-ups mentioned</strong>
              <ul className="list-disc pl-4 mt-2">
                {tasks.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
